import { CardActions, Button } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Renders a custom action bar component for a date picker.
 * This component renders action buttons like cancel, accept, and clear.
 * It also manages the "disabled" state of the accept button until a date is selected.
 *
 * @param {Object} props - The props object containing the following properties:
 * @param {string[]} props.actions - An array of strings representing the predefined actions to render buttons for.
 * @param {boolean} props.value - A boolean indicating whether a value is selected (used to enable/disable the accept button).
 * @param {Function} props.onCancel - The function to call when the cancel button is clicked.
 * @param {Function} props.onAccept - The function to call when the accept button is clicked.
 * @param {Function} props.onClear - The function to call when the clear button is clicked.
 * @returns {JSX.Element} JSX element representing the custom action bar component for the date picker.
 */
const DatePickerActionBar = (props) => {
  const { i18n, t } = useTranslation("Shared");

  const predefinedActionButtons = React.useMemo(
    () => ({
      cancel: (props) => (
        <Button onClick={props.onCancel}>{t("buttons.cancel")}</Button>
      ),
      accept: (props) => (
        <Button disabled={!props.value} onClick={props.onAccept}>
          {t("buttons.datepicker_accept")}
        </Button>
      ),
      clear: (props) => (
        <Button onClick={props.onClear}>{t("buttons.clear")}</Button>
      ),
    }),
    [i18n.language]
  );

  return (
    <CardActions
      className="MuiPickersLayout-actionBar"
      sx={{ justifyContent: "flex-end" }}
    >
      {props.actions.map((action, idx) => (
        <React.Fragment key={idx}>
          {predefinedActionButtons[action](props)}
        </React.Fragment>
      ))}
    </CardActions>
  );
};

export default DatePickerActionBar;
